import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { GenderModel } from '@utmost.co/enterprise-models';
import { OptionTypeBase, FormLabel, Dialog, Select, Button, Input } from '@beeline.com/bp-design-system';
import { useStaticGlobalIntl } from 'utils/intl/useStaticGlobalIntl';
import { useAddGenders } from '../hooks/useAddGenders';
import { usePatchGenders } from '../hooks/usePatchGenders';
import { useGenderIntl } from '../translations/useGenderIntl';
interface Props {
  open: boolean;
  modelToEdit: GenderModel | null;
  onClose(): void;
  onDone: () => void;
}
const options: OptionTypeBase<boolean>[] = [
  { label: 'Enabled', value: true },
  { label: 'Disabled', value: false }
];
export const AddEditGenderModal: FC<React.PropsWithChildren<Props>> = ({ open, modelToEdit, onClose, onDone }) => {
  const { addGender, loading: isAdding } = useAddGenders();
  const { patchGender, loading: isPatching } = usePatchGenders();
  const {
    name: nameLabel,
    referenceId: referenceIdLabel,
    assignable: assignableLabel,
    createTitle,
    createSubTitle,
    updateTitle,
    updateSubTitle
  } = useGenderIntl();
  const title = modelToEdit ? updateTitle : createTitle;
  const subTitle = modelToEdit ? updateSubTitle : createSubTitle;
  const [submitLabel] = useStaticGlobalIntl('general.Submit', 'general.Cancel');
  const [gender, setGender] = useState<Partial<GenderModel>>(
    modelToEdit
      ? { ...modelToEdit }
      : {
          assignable: true,
          status: 'ACTIVE'
        }
  );
  // Refresh state when modal is opened
  useEffect(() => {
    if (open)
      setGender(
        modelToEdit
          ? { ...modelToEdit }
          : {
              assignable: true,
              status: 'ACTIVE'
            }
      );
  }, [modelToEdit, open]);
  const closeForm = useCallback(() => {
    onClose();
  }, [onClose]);
  const submitForm = useCallback(async () => {
    const { uid, assignable, name, referenceId } = gender as GenderModel;
    if (modelToEdit) {
      await patchGender(uid!, {
        assignable,
        name,
        referenceId
      });
    } else {
      await addGender({
        referenceId,
        name,
        assignable
      });
    }
    onDone();
  }, [addGender, onDone, modelToEdit, patchGender, gender]);
  const canContinue = useMemo(() => {
    if (!gender.name) return false;
    return true;
  }, [gender]);
  const isUtmostResource = gender.sourceName === 'UTMOST' || gender.sourceName === undefined;
  return (
    <Dialog
      open={open}
      onClose={closeForm}
      dataTest="create-termination-reason-dialog"
      title={title}
      subTitle={subTitle}
      show={{ cancelButton: true }}
      disableBackdropClick
      actions={[
        <Button
          dataTest="confirm-button"
          variant="contained"
          key="confirm-button"
          color="primary"
          disabled={!canContinue || isAdding || isPatching}
          onClick={submitForm}
        >
          {submitLabel}
        </Button>
      ]}
    >
      <StyledModalContent>
        <div className="form">
          <div className="field">
            <FormLabel required htmlFor="name_input">
              {nameLabel}
            </FormLabel>
            <Input
              id="name_input"
              dataTest="name_input"
              value={gender.name || ''}
              fullWidth
              onChange={(e) => setGender({ ...gender, name: e.target.value })}
            />
          </div>
          <div className="field">
            <FormLabel htmlFor="reference_id_input">{referenceIdLabel}</FormLabel>
            <Input
              dataTest="reference_id_input"
              id="reference_id_input"
              disabled={!isUtmostResource}
              value={gender.referenceId || ''}
              fullWidth
              onChange={(e) => setGender({ ...gender, referenceId: e.target.value })}
            />
          </div>

          <div className="field">
            <FormLabel htmlFor="assignable_input">{assignableLabel}</FormLabel>
            <Select<boolean>
              dataTest="assignable_input"
              id="assignable_input"
              value={gender.assignable}
              options={options}
              onChange={(option) => {
                setGender({ ...gender, assignable: option || false });
              }}
            />
          </div>
        </div>
      </StyledModalContent>
    </Dialog>
  );
};
const StyledModalContent = styled.div`
  .form {
    .field {
      margin-bottom: 1rem;

      label {
        margin: 0 0 6px 0;
      }
    }
  }
`;
